import { sentenceCase } from 'change-case'
import { Trash2Icon } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { AWXCardLimitsInterval } from '@ankor-io/common/types/awx/card'

import type { CardEditFormData } from '@/pages/CardEdit'
import { Button } from '@/ui-components/Button'
import { Field, Label } from '@/ui-components/Fieldset'
import { Input } from '@/ui-components/Input'
import { Select } from '@/ui-components/Select'

type CardLimitRowProps = {
  index: number
  form: ReturnType<typeof useForm<CardEditFormData>>
  onRemove: () => void
  canRemove: boolean
}

export const CardLimitRow = ({ index, form, onRemove, canRemove = true }: CardLimitRowProps) => {
  const getFieldError = (fieldName: 'amount' | 'interval') => {
    return form.formState.errors.authorization_controls?.transaction_limits?.limits?.[index]?.[fieldName]
  }

  const hasErrors = getFieldError('amount') || getFieldError('interval')

  return (
    <div className="space-y-3 mb-4">
      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <Field>
            <Label>Amount</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="Amount"
              {...form.register(`authorization_controls.transaction_limits.limits.${index}.amount`, {
                valueAsNumber: true,
              })}
              data-testid="amount-input"
            />
          </Field>
        </div>

        <div className="w-48">
          <Field>
            <Label>Interval</Label>
            <Select
              {...form.register(`authorization_controls.transaction_limits.limits.${index}.interval`)}
              data-testid="interval-select"
            >
              {Object.values(AWXCardLimitsInterval).map((interval) => (
                <option
                  key={interval}
                  value={interval}
                >
                  {sentenceCase(interval)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {canRemove && (
          <div className="w-10">
            <Button
              type="button"
              plain
              onClick={onRemove}
              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              aria-label="Remove limit"
            >
              <Trash2Icon data-slot="icon" />
            </Button>
          </div>
        )}
      </div>

      {/* Error messages for this limit row */}
      {hasErrors && (
        <div className="flex gap-4">
          <div className="flex-1">
            {getFieldError('amount') && (
              <div className="text-base/6 sm:text-sm/6 text-red-600 dark:text-red-500 data-disabled:opacity-50">
                {getFieldError('amount')?.message}
              </div>
            )}
          </div>
          <div className="w-48">
            {getFieldError('interval') && (
              <div className="text-base/6 sm:text-sm/6 text-red-600 dark:text-red-500 data-disabled:opacity-50">
                {getFieldError('interval')?.message}
              </div>
            )}
          </div>
          {canRemove && <div className="w-10">{/* Spacer for the remove button */}</div>}
        </div>
      )}
    </div>
  )
}
